<script lang="ts" setup>
    import { PropType, watch } from 'vue'
    import { ref, computed } from 'vue'
    import { useEditStateStore } from '@/js/stores/editState'

    type Entry = {
        id: string
        label: string
    }

    const props = defineProps({
        editKey: {
            type: String,
            required: true
        },
        entries: {
            type: Array as PropType<Entry[]>,
            required: false,
            default: () => []
        },
        autoCompleteData: {
            type: Array as PropType<Entry[]>,
            required: false,
            default: () => []
        },
        title: {
            type: String,
            default: ''
        },
        showTitle: {
            type: Boolean,
            required: false,
            default: true
        },
        toolTip: {
            type: String,
            required: false,
            default: () => ''
        },
        allowEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        handleIds: {
            type: Boolean,
            required: false,
            default: true
        }
    })
    const emit = defineEmits(['textSaved', 'editAborted', 'editStarted'])
    let editEntries = ref<string[]>([])
    const editStore = useEditStateStore()
    watch(
        () => props.editKey,
        (newKey) => {
            editStore.register([newKey])
        },
        { immediate: true }
    )
    const isEditing = computed(() => editStore.editStates[props.editKey])

    function startEditing() {
        editStore.set(props.editKey, true)
        editEntries.value = props.entries.map((entry) => entry.label)
    }

    function stopEditing() {
        editStore.set(props.editKey, false)
        editEntries.value = []
    }

    const isLoading = computed(() => store.getters['isLoading'])

    const entriesLabel = computed(() => {
        return props.entries?.map((entry) => entry.label).join(', ')
    })
    const autoCompleteLabels = computed(() => {
        return props.autoCompleteData?.map((entry) => entry.label)
    })

    const handleEditClick = () => {
        if (isLoading.value) {
            return
        }
        emit('editStarted')
        startEditing()
    }

    const handleAbortClick = () => {
        if (isLoading.value) {
            return
        }
        emit('editAborted')
        stopEditing()
    }

    const handleSaveClick = () => {
        if (isLoading.value) {
            return
        }
        // emit changed values to parent component or store
        // should emit iri or ids
        if (props.handleIds) {
            let allEntries: Entry[] = [...props.entries, ...props.autoCompleteData]
            editEntries.value = editEntries.value.map(
                (editEntry) =>
                    allEntries.find((allEntry) => editEntry.trim() === allEntry.label.trim()).id
            )
        }
        emit('textSaved', editEntries.value)
        stopEditing()
    }
</script>

<template>
    <v-form novalidate @submit.prevent>
        <div class="d-flex flex-nowrap align-stretch">
            <common-input-header :title="title" :show-title="showTitle" :tool-tip="toolTip" />
            <div v-if="isEditing" class="w-100">
                <slot name="edit-component">
                    <common-combobox-field
                        v-model="editEntries"
                        label=""
                        class="h-100 w-10 pa-0"
                        :is-loading="isLoading"
                        :suggestions="autoCompleteLabels"
                        :tool-tip="toolTip"
                    />
                </slot>
            </div>
            <div
                v-else
                class="d-flex align-center pa-2 w-100 border-primary border border-opacity-100"
            >
                {{ entriesLabel ? entriesLabel : '-' }}
            </div>
            <v-btn
                v-if="allowEdit && !isEditing"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                icon="mdi-lead-pencil"
                @click="handleEditClick"
                :data-test="`${props.editKey}-edit-button`"
            />
            <slot v-if="isEditing" name="create-user-dialog" />
            <v-btn
                v-if="allowEdit && isEditing"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                icon="mdi-check"
                @click="handleSaveClick"
            />
            <v-btn
                v-if="allowEdit && isEditing"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                icon="mdi-cancel"
                @click="handleAbortClick"
            />
        </div>
    </v-form>
</template>

<script></script>

<style scoped lang="scss"></style>
<style lang="scss">
    input::-webkit-calendar-picker-indicator {
        display: none;
        opacity: 0;
    }
</style>
